import { z } from "zod";

import { getNormalEnum } from "./pseudoEnums.js";

export const DATE_FORMAT_WITH_TIME = "YYYY-MM-DD hh:mm A";
export const DATE_FORMAT_NO_TIME = "YYYY-MM-DD";

export const DateAndTime = z.object({
  isoDateString: z.string(),
  timezoneName: z.string(),
});
export const DateOnly = z.object({
  dateString: z.string(),
});

export const RelativeDateUnitLiteral = z.enum([
  "DAY",
  "WEEK",
  "MONTH",
  "QUARTER",
  "YEAR",
]);

export type RelativeDateUnit = z.infer<typeof RelativeDateUnitLiteral>;
export const RelativeDateUnit = getNormalEnum(RelativeDateUnitLiteral);

export const RelativeDateOperationLiteral = z.enum(["AGO", "FROM_NOW"]);

export type RelativeDateOperation = z.infer<
  typeof RelativeDateOperationLiteral
>;
export const RelativeDateOperation = getNormalEnum(
  RelativeDateOperationLiteral,
);

export const RelativeDatetimeOffsetValue = z.object({
  displayName: z.string().optional(),
  unit: RelativeDateUnitLiteral,
  amount: z.number(),
  operation: RelativeDateOperationLiteral,
  timezone: z.string(),
});

export type RelativeDatetimeOffsetValue = z.infer<
  typeof RelativeDatetimeOffsetValue
>;

export const DatetimeValue = z.union([
  DateAndTime,
  DateOnly,
  RelativeDatetimeOffsetValue,
]);

export type DatetimeValue = z.infer<typeof DatetimeValue>;

// eslint-disable-next-line tree-shaking/no-side-effects-in-initialization
export const StringDate = z
  .string()
  .brand("stringDate")
  .refine(
    (s) => !isNaN(new Date(s).getTime()), // Ensure the string is Date parsable.
  );
export type StringDate = z.infer<typeof StringDate>;

/**
 *
 * @param flagValue the value of the LD flag
 * @param date the date to compare against the flag value
 * @returns true ("enabled") if date >= flagValue, or if flagValue is null/undefined
   (no cutoff configured means "no lower bound", so the date always qualifies);
   false ("disabled") otherwise (or if flagValue is explicitly set to "DISABLED").
   Will throw error if unable to parse date from LD flag value.
 */
export const evaluateMinDateFlag = (
  flagValue: string | null | undefined,
  date: Date,
): boolean => {
  if (flagValue === "DISABLED") {
    return false;
  }

  // No cutoff configured (null/undefined) means "no lower bound" — the date
  // always qualifies. For the disableFallback gate this makes an unset
  // min-date disable the fallback for everyone, rather than never.
  if (flagValue == null) {
    return true;
  }

  const minDate = new Date(flagValue);
  if (isNaN(minDate.getTime())) {
    throw new Error("Invalid date");
  }

  return date.getTime() >= minDate.getTime();
};
